import React, { useMemo, useState } from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { formatIDR } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Trash2, Search } from "lucide-react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

export default function History() {
  const { data: txs } = useSWR("/transactions", fetcher);
  const { data: accounts } = useSWR("/accounts", fetcher);
  const [q, setQ] = useState("");
  const [filterType, setFilterType] = useState("all");

  const accMap = useMemo(() => {
    const m = {};
    (accounts || []).forEach((a) => (m[a.id] = a.name));
    return m;
  }, [accounts]);

  const filtered = (txs || []).filter((t) => {
    if (filterType !== "all" && t.type !== filterType) return false;
    if (q) {
      const s = q.toLowerCase();
      return (
        t.category?.toLowerCase().includes(s) ||
        t.note?.toLowerCase().includes(s) ||
        accMap[t.account_id]?.toLowerCase().includes(s)
      );
    }
    return true;
  });

  const del = async (id) => {
    if (!confirm("Hapus transaksi ini?")) return;
    try {
      await api.delete(`/transactions/${id}`);
      toast.success("Transaksi dihapus");
      mutate("/transactions");
      mutate("/accounts");
      mutate("/reports/summary");
    } catch { toast.error("Gagal menghapus"); }
  };

  return (
    <div className="animate-fade-up">
      <div className="flex items-baseline justify-between">
        <div>
          <p className="label-caps">Log</p>
          <h1 className="font-heading text-3xl md:text-4xl font-medium tracking-tight mt-1">Transaction History</h1>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mt-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Cari kategori, catatan, akun…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-11"
            data-testid="history-search"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="h-11 sm:w-48" data-testid="history-filter-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Tipe</SelectItem>
            <SelectItem value="income">Income</SelectItem>
            <SelectItem value="expense">Expense</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6 border border-border bg-card overflow-hidden">
        <div className="grid grid-cols-12 label-caps px-4 py-3 border-b border-border bg-secondary/40">
          <div className="col-span-3">Tanggal</div>
          <div className="col-span-3">Kategori</div>
          <div className="col-span-3">Akun</div>
          <div className="col-span-2 text-right">Jumlah</div>
          <div className="col-span-1"></div>
        </div>
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">Tidak ada transaksi.</div>
        ) : (
          filtered.map((t) => (
            <div key={t.id} className="grid grid-cols-12 px-4 py-3 border-b border-border last:border-0 items-center hover:bg-accent/40 transition-colors" data-testid={`tx-row-${t.id}`}>
              <div className="col-span-3 text-sm">{t.date}</div>
              <div className="col-span-3 text-sm">
                <div className="font-medium">{t.category}</div>
                <div className="text-xs text-muted-foreground truncate">{t.note}</div>
              </div>
              <div className="col-span-3 text-sm text-muted-foreground">{accMap[t.account_id] || "—"}</div>
              <div className={`col-span-2 text-right font-mono text-sm ${t.type === "income" ? "text-gain" : "text-loss"}`}>
                {t.type === "income" ? "+" : "-"}{formatIDR(t.amount)}
              </div>
              <div className="col-span-1 text-right">
                <Button variant="ghost" size="icon" onClick={() => del(t.id)} data-testid={`tx-delete-${t.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
